<template>
  <section class="changes-view">
    <header class="changes-header">
      <h1>Recent changes</h1>
      <p>Daily digest of document edits across projects you can view history for.</p>
    </header>

    <div v-if="error" class="workspace-alert">{{ error }}</div>

    <div v-if="loading" class="changes-empty">Loading changes…</div>
    <div v-else-if="days.length === 0" class="changes-empty">
      <History class="icon" />
      <span>No document changes yet.</span>
    </div>

    <div
      v-for="day in days"
      v-else
      :key="String(day.windowStartAtUtc)"
      class="change-run"
    >
      <header class="change-run-head">
        <h2>{{ formatInstantDate(day.windowStartAtUtc) }}</h2>
        <span class="run-meta">{{ day.documents.length }} document{{ day.documents.length === 1 ? '' : 's' }}</span>
      </header>
      <p class="run-window">{{ formatInstantDate(day.windowStartAtUtc) }} — {{ formatInstantDate(day.windowEndAtUtc) }}</p>

      <template v-for="group in groupedDocuments(day.documents)" :key="group.projectId">
        <p class="change-project">{{ group.projectName }}</p>
        <DocumentChangeCard
          v-for="document in group.documents"
          :key="`${day.windowStartAtUtc}-${document.documentId}`"
          :summary="document"
          :window-start-at-utc="day.windowStartAtUtc"
          :window-end-at-utc="day.windowEndAtUtc"
        />
      </template>
    </div>

    <button
      v-if="hasMore && !loading"
      type="button"
      class="btn change-load-more"
      :disabled="loadingMore"
      @click="loadMore"
    >
      <span>{{ loadingMore ? 'Loading…' : 'Load more' }}</span>
    </button>
  </section>
</template>

<script setup lang="ts">
import { History } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
import type { EntityId } from '../api/entityId';
import {
  QueriesService,
  type GetDocumentChangeFeedQueryResponse,
  type DocumentChangeFeedDayResponse,
  type DocumentChangeFeedDocumentResponse,
  type Instant
} from '../api/generated';
import { formatInstantDate } from '../format';
import { getErrorMessage, useSessionStore } from '../stores/session';
import DocumentChangeCard from '../components/DocumentChangeCard.vue';

const PageSizeDays = 14;

interface DocumentGroup {
  projectId: EntityId;
  projectName: string;
  documents: DocumentChangeFeedDocumentResponse[];
}

const days = ref<DocumentChangeFeedDayResponse[]>([]);
const session = useSessionStore();
const loading = ref(true);
const loadingMore = ref(false);
const error = ref<string | null>(null);
const nextBeforeUtc = ref<Instant | null>(null);
const hasMore = ref(false);

onMounted(async () => {
  try {
    const bootstrap = session.pageBootstrap?.changes ?? null;
    if (bootstrap != null) {
      applyPage(bootstrap, null);
    } else {
      await loadPage();
    }
  } catch (caught) {
    error.value = getErrorMessage(caught, 'Unable to load changes.');
  } finally {
    loading.value = false;
  }
});

async function loadPage(beforeUtc: Instant | null = null): Promise<void> {
  const response = await QueriesService.queryGetDocumentChangeFeedQuery({
    requestBody: {
      timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
      beforeUtc,
      limitDays: PageSizeDays
    }
  });
  applyPage(response, beforeUtc);
}

function applyPage(response: GetDocumentChangeFeedQueryResponse, beforeUtc: Instant | null): void {
  days.value = beforeUtc == null ? response.days : [...days.value, ...response.days];
  nextBeforeUtc.value = response.nextBeforeUtc;
  hasMore.value = response.hasMore;
}

async function loadMore(): Promise<void> {
  if (loadingMore.value || !hasMore.value) {
    return;
  }

  loadingMore.value = true;
  error.value = null;
  try {
    await loadPage(nextBeforeUtc.value);
  } catch (caught) {
    error.value = getErrorMessage(caught, 'Unable to load more changes.');
  } finally {
    loadingMore.value = false;
  }
}

function groupedDocuments(documents: DocumentChangeFeedDocumentResponse[]): DocumentGroup[] {
  const groups = new Map<EntityId, DocumentGroup>();
  for (const document of documents) {
    let group = groups.get(document.projectId);
    if (!group) {
      group = { projectId: document.projectId, projectName: document.projectName, documents: [] };
      groups.set(document.projectId, group);
    }

    group.documents.push(document);
  }

  for (const group of groups.values()) {
    group.documents.sort((left, right) =>
      String(right.lastEditedAt).localeCompare(String(left.lastEditedAt)));
  }

  return [...groups.values()].sort((left, right) =>
    left.projectName.localeCompare(right.projectName, undefined, { sensitivity: 'base' }));
}
</script>
